import { dp } from '@djs';
import { decodeHTMLEntities } from './decodeHTMLEntities';
import { RequiredFormatKey, RequiredFormatKeyPaths } from './formatKeysDefaultPaths';

export interface RawResourceLanguage {
  keys: {
    [key: string]: any;
  };
  config: {
    [key: string]: any;
  };
}

const COMMON_PREFIX = 'vkui_common';

const LanguageNamespace = {
  // Ключи из объекта keys с основным префиксом для текущего проекта (указанным в defaultNsPrefix)
  TRANSLATION: 'translation',
  // Ключи из объекта keys с общим префиксом (указанным в COMMON_PREFIX)
  COMMON: 'common',
  // Ключи для работы коллекции формат-функций
  FORMAT: 'format',
} as const;

type LanguageNamespace = Union<typeof LanguageNamespace>;

function getNamespaceKey(key: string, nsPrefix: string): [LanguageNamespace, string] | null {
  if (key.startsWith(COMMON_PREFIX)) {
    return [LanguageNamespace.COMMON, key.slice(COMMON_PREFIX.length + 1)];
  }

  if (key.startsWith(nsPrefix)) {
    return [LanguageNamespace.TRANSLATION, key.slice(nsPrefix.length + 1)];
  }

  return null;
}

function prepareInterpolation(value: string): string {
  return value.replace(/%s/, '{count}');
}

function getPluralForms(value: any): [string, string][] {
  const arrifiedValue = Array.isArray(value) ? Array.from(value).splice(1) : [value];
  const values = arrifiedValue.map(prepareInterpolation);

  switch (values.length) {
    case 1:
      return [['', values[0]]];
    case 2:
      return [
        ['', values[0]],
        ['_plural', values[1]],
      ];
    default:
      return values.map((value, index) => [`_${index}`, value]);
  }
}

function makeFormatNamespace(rawResourceLanguage: RawResourceLanguage, formatKeysPaths: RequiredFormatKeyPaths) {
  const formatNamespace: { [key: string]: string | string[] } = {};

  for (const formatKey in formatKeysPaths) {
    formatNamespace[formatKey] = dp(rawResourceLanguage, formatKeysPaths[formatKey as RequiredFormatKey]);

    if (['hoursAgo', 'minsAgo'].includes(formatKey)) {
      const pluralForms = getPluralForms(formatNamespace[formatKey]);

      pluralForms.forEach(([pluralSuffix, pluralValue]) => {
        formatNamespace[`${formatKey}${pluralSuffix}`] = decodeHTMLEntities(pluralValue);
      });
    }
  }

  return formatNamespace;
}

/**
 * Преобразует сырые данные словаря переводов ВК к данным в формате i18next
 * @param rawResourceLanguage сырые данные словаря переводов ВК
 * @param formatKeysPaths пути к ключам, участвующим в форматировании дат, строк и чисел
 * @param defaultNsPrefix основной префикс ключей переводов, относящихся к проекту
 * @param defaultNs название нэймспейса по умолчанию
 */
export function formatResourceLanguage(
  rawResourceLanguage: RawResourceLanguage,
  formatKeysPaths: RequiredFormatKeyPaths,
  defaultNsPrefix: string,
  defaultNs: string,
) {
  const resource: { [key: string]: { [key: string]: string | string[] } } = {
    [LanguageNamespace.FORMAT]: {},
    [LanguageNamespace.COMMON]: {},
    [LanguageNamespace.TRANSLATION]: {},
  };

  // необходимые для форматирования ключи кладем в FORMAT

  resource[LanguageNamespace.FORMAT] = makeFormatNamespace(rawResourceLanguage, formatKeysPaths);

  // rawResourceLanguage.keys разбиваем по префиксам, делаем преобразования и кладем их в TRANSLATION и COMMONS

  for (const key in rawResourceLanguage.keys) {
    const namespaceKey = getNamespaceKey(key, defaultNsPrefix);

    if (namespaceKey) {
      const [ns, nsKey] = namespaceKey;
      const pluralForms = getPluralForms(rawResourceLanguage.keys[key]);

      pluralForms.forEach(([pluralSuffix, pluralValue]) => {
        resource[ns][`${nsKey}${pluralSuffix}`] = decodeHTMLEntities(pluralValue);
      });
    }
  }

  // Кладем ключи из TRANSLATION в нэймспейс по умолчанию

  if (defaultNs !== LanguageNamespace.TRANSLATION) {
    resource[defaultNs] = resource[LanguageNamespace.TRANSLATION];
    delete resource[LanguageNamespace.TRANSLATION];
  }

  return resource;
}
